import { isStatusField, isSubStatusField } from './formStudioFormUtils';

function normalizeKey(value) {
  return String(value || '').trim();
}

export function getStatusFieldKeys(fields = []) {
  return new Set(
    fields
      .filter(isStatusField)
      .map((field) => normalizeKey(field.key_name))
      .filter(Boolean)
  );
}

/**
 * Returns whether a sub-status belongs to a parent Status field.
 * Legacy orphan links are adopted only when there is exactly one possible
 * Status parent, which keeps the repair deterministic.
 */
export function isSubStatusLinkedToParent(
  subStatusField,
  fields,
  parentField,
  previousParentKey = ''
) {
  if (!isSubStatusField(subStatusField) || !isStatusField(parentField)) {
    return false;
  }

  const childParentKey = normalizeKey(subStatusField.status_parent_key);
  const parentKeys = new Set([
    normalizeKey(parentField.key_name),
    normalizeKey(previousParentKey),
  ]);
  parentKeys.delete('');

  if (parentKeys.has(childParentKey)) {
    return true;
  }

  const statusFields = (fields || []).filter(isStatusField);
  const validStatusKeys = getStatusFieldKeys(fields);
  const isOrphan = childParentKey && !validStatusKeys.has(childParentKey);
  return statusFields.length === 1 && isOrphan;
}

export function getLinkedSubStatusEntries(
  fields = [],
  parentField,
  previousParentKey = ''
) {
  return fields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) =>
      isSubStatusLinkedToParent(field, fields, parentField, previousParentKey)
    );
}

/**
 * Saves a Status field and cascades key renames to linked sub-status fields.
 * It also repairs legacy orphan links when the parent is unambiguous.
 */
export function updateStatusFieldAndLinks(fields = [], parentIndex, nextParent) {
  const currentParent = fields[parentIndex];
  if (!currentParent) {
    return fields;
  }

  const nextParentKey = normalizeKey(nextParent?.key_name);
  return fields.map((field, index) => {
    if (index === parentIndex) {
      return { ...currentParent, ...nextParent };
    }

    if (
      nextParentKey &&
      isSubStatusLinkedToParent(field, fields, nextParent, currentParent.key_name)
    ) {
      return { ...field, status_parent_key: nextParentKey };
    }

    return field;
  });
}
